import { Injectable } from '@angular/core';
import { AssetDetailsResponse } from '@ddt-web/common';
import { InjectConfig, PeConfigKey } from '@ddt-web/config-layer';
import { PopupErrorObject } from '@ddt-web/error-handler';
import { AssetResolveResponse } from '@ddt-web/pas';
import { AccountErrors, ApplicationStateStoreProxyService, PartnerAccount, PartnerPosition } from '@ddt-web/shared-stores';

import { InvalidPartnerPortfolioTypeMapping, PartnerPlatformConstants } from '../constants/partner-platform.constants';

@Injectable({
	providedIn: 'root'
})
export class PartnerPortfolioValidatorService {
	@InjectConfig(PeConfigKey.HOLDING_WEIGHT_LIMIT) private readonly maxHoldingPercentage: number = 75;
	private readonly cashAladdinId: string = 'XUSD00000';

	constructor(
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService
	) { }

	public validatePortfolios(accounts: PartnerAccount[], resolvedAssets: AssetResolveResponse): Map<string, AccountErrors> {
		const accountErrorMap: Map<string, AccountErrors> = new Map();
		accounts.forEach(account => {
			const exceedsMaxHoldingPercentage = this.validateMaxHoldingsPercentage(account.positions);
			const hasDuplicateMorningstarFundIds = this.validateMorningstarFundIds(account.positions, resolvedAssets);
			const hasTooMuchCash = this.validateTooMuchCash(account.positions);
			const hasPortfolioErrors = exceedsMaxHoldingPercentage || hasDuplicateMorningstarFundIds || hasTooMuchCash || account.positions.length <= 1;
			const accountErrors: AccountErrors = {
				containsUnresolvedSecurities: account.positions.some(position => !resolvedAssets.assets.get(position.assetId)),
				hasMarketValueErrors: account.metadata['hasMarketValueErrors']?.stringValue === 'true',
				hasExactlyOneHolding: account.positions.length === 1,
				hasZeroHoldings: account.positions.length === 0,
				exceedsMaxHoldingPercentage,
				hasDuplicateMorningstarFundIds,
				hasTooMuchCash,
				hasPortfolioErrors
			};
			accountErrorMap.set(account.id, accountErrors);
		});

		this.applicationStateStoreProxyService.updateImportPartnerPortfoliosAccountErrorMap(accountErrorMap);
		return accountErrorMap;
	}

	/**
	 * aggregates resolved holdings market value based on asset id, and validates them if they satisfy the predefined constraints
	 *
	 * @param {AssetDetailsResponse[]} holdings partner portfolios
	 * 
	 * @returns {AccountErrors} invalidPortfolioErrorKey
	 * @memberof PartnerPortfolioValidatorService
	 */
	public aggregateAndValidateHoldings(holdings: AssetDetailsResponse[]): AccountErrors {
		const positions = holdings.reduce((accumulator, currHolding) => {
			const holdingIndex = accumulator.findIndex(position => position.assetId === currHolding.assets[0].assetId);
			if (holdingIndex > -1) {
				accumulator[holdingIndex].marketValue += currHolding.value;
			} else {
				accumulator.push({
					assetId: currHolding.assets[0].assetId,
					marketValue: currHolding.value
				});
			}
			return accumulator;
		}, []);

		const exceedsMaxHoldingPercentage = this.validateMaxHoldingsPercentage(positions);
		const hasTooMuchCash = this.validateTooMuchCash(positions);
		let hasDuplicateMorningstarFundIds = false;
		// If a portfolio has < 4 holdings, check for duplicate morningstarFundIds
		if (positions?.length < 4) {
			const morningstarFundIds = positions.map(position => holdings.find(holding => holding.assets[0].assetId === position.assetId)?.assets[0]?.morningstarFundId)
				.filter(fundId => !!fundId);
			hasDuplicateMorningstarFundIds = new Set(morningstarFundIds).size !== morningstarFundIds.length;
		}
		const hasPortfolioErrors = exceedsMaxHoldingPercentage || hasDuplicateMorningstarFundIds || hasTooMuchCash || positions.length <= 1;
		return {
			hasExactlyOneHolding: positions.length === 1,
			exceedsMaxHoldingPercentage,
			hasDuplicateMorningstarFundIds,
			hasTooMuchCash,
			hasPortfolioErrors
		};
	}

	public validatePortfoliosAndThrowErrors(
		updatedAccounts: PartnerAccount[],
		resolvedAssets: AssetResolveResponse,
		partnerPortfolios: any[],
		mode: string = 'NEW',
		isRefreshFlow: boolean = false
	): Map<string, AccountErrors> {
		const accountErrorMap = this.validatePortfolios(updatedAccounts, resolvedAssets);
		partnerPortfolios.forEach(partnerPortfolio => {
			const errorInfo = accountErrorMap.get(partnerPortfolio.encrypted.originPartnerAccountId);
			if (errorInfo.hasExactlyOneHolding) {
				this.throwError(
					partnerPortfolio,
					InvalidPartnerPortfolioTypeMapping.ONE_HOLDING_ERROR.errorCode,
					InvalidPartnerPortfolioTypeMapping.ONE_HOLDING_ERROR.errorText,
					mode,
					isRefreshFlow
				);
			} else if (errorInfo.hasZeroHoldings) {
				this.throwError(
					partnerPortfolio,
					InvalidPartnerPortfolioTypeMapping.ZERO_HOLDINGS_ERROR.errorCode,
					InvalidPartnerPortfolioTypeMapping.ZERO_HOLDINGS_ERROR.errorText,
					mode,
					isRefreshFlow
				);
			} else if (errorInfo.exceedsMaxHoldingPercentage) {
				this.throwError(
					partnerPortfolio,
					InvalidPartnerPortfolioTypeMapping.EXCEEDS_MAX_HOLDING_PERCENTAGE_ERROR.errorCode,
					InvalidPartnerPortfolioTypeMapping.EXCEEDS_MAX_HOLDING_PERCENTAGE_ERROR.errorText,
					mode,
					isRefreshFlow
				);
			} else if (errorInfo.hasDuplicateMorningstarFundIds) {
				this.throwError(
					partnerPortfolio,
					InvalidPartnerPortfolioTypeMapping.DUPLICATED_MORNINGSTAR_FUND_IDS_ERROR.errorCode,
					InvalidPartnerPortfolioTypeMapping.DUPLICATED_MORNINGSTAR_FUND_IDS_ERROR.errorText,
					mode,
					isRefreshFlow
				);
			} else if (errorInfo.hasTooMuchCash) {
				this.throwError(
					partnerPortfolio,
					InvalidPartnerPortfolioTypeMapping.TOO_MUCH_CASH_ERROR.errorCode,
					InvalidPartnerPortfolioTypeMapping.TOO_MUCH_CASH_ERROR.errorText,
					mode,
					isRefreshFlow
				);
			}
		});
		return accountErrorMap;
	}

	public buildInvalidPortfolioErrorKey(errorInfo: AccountErrors): string {
		if (errorInfo.hasExactlyOneHolding) {
			return PartnerPlatformConstants.ONE_HOLDING_ERROR;
		} else if (errorInfo.hasZeroHoldings) {
			return PartnerPlatformConstants.ZERO_HOLDINGS_ERROR;
		} else if (errorInfo.exceedsMaxHoldingPercentage) {
			return PartnerPlatformConstants.EXCEEDS_MAX_HOLDING_PERCENTAGE_ERROR;
		} else if (errorInfo.hasDuplicateMorningstarFundIds) {
			return PartnerPlatformConstants.DUPLICATED_MORNINGSTAR_FUND_IDS_ERROR;
		} else if (errorInfo.hasTooMuchCash) {
			return PartnerPlatformConstants.TOO_MUCH_CASH_ERROR;
		} else {
			return null;
		}
	}

	private validateMaxHoldingsPercentage(positions: PartnerPosition[]): boolean {
		const totalPortfolioValue = positions.reduce(
			(accumulator, position) => accumulator + position.marketValue,
			0
		);

		return positions.some(pos => (pos.marketValue / totalPortfolioValue) * 100 > this.maxHoldingPercentage);
	}

	private validateMorningstarFundIds(positions: PartnerPosition[], assetsResponse: AssetResolveResponse): boolean {
		// If a portfolio has < 4 holdings, check for duplicate morningstarFundIds
		if (positions?.length < 4) {
			const morningstarFundIds = positions.map(pos => assetsResponse.assets.get(pos.assetId)?.funds?.morningstarFundId?.stringValue).filter(fundId => !!fundId);
			return new Set(morningstarFundIds).size !== morningstarFundIds.length;
		}
		return false;
	}

	private validateTooMuchCash(positions: PartnerPosition[]): boolean {
		const posIds = positions.map(pos => pos.assetId);
		if (posIds.includes(this.cashAladdinId) && posIds.filter(posId => posId === 'XUNKNOWNX').length <= 1) {
			return new Set(posIds).size < 3;
		} else {
			return false;
		}
	}

	private throwError(partnerPortfolio, errorCode: string, errorText: { key: string; text: string }, mode: string, isRefreshFlow: boolean): void {
		const id = isRefreshFlow ? partnerPortfolio.id : partnerPortfolio.uniqueId;
		setTimeout(() => {
			throw new PopupErrorObject(
				{
					...partnerPortfolio,
					errorCode,
					errorText,
					description: partnerPortfolio.description,
					id,
					mode
				});
		}, 100);
	}
}
